import { parseCalendarDate } from '@/lib/calendarDate';
import { isReportAppLink } from '../siteLinks.js';
import { lengthText } from '../siteLength.js';

// Helpers every Trailhead block uses. Kept here so forty components do
// not each grow their own copy of "which colour is caution" — the
// four-baselines problem with fewer digits.

// Editor Style overrides → the CSS variables trailhead.css reads.
// Classic's style schemas name keys per block (eyebrow, heading,
// title, subtitle …); Trailhead honours the ones that mean the same
// thing everywhere, so a colour set under either template still
// lands somewhere sensible after a switch.
export function styleVars(config) {
    const s = config?.style || {};
    const v = {};
    if (s.bg) v['--block-bg'] = s.bg;
    if (s.text) v['--block-text'] = s.text;
    if (s.eyebrow) v['--text-eyebrow'] = s.eyebrow;
    const heading = s.heading || s.title;
    if (heading) v['--text-heading'] = heading;
    const sub = s.subtitle || s.body;
    if (sub) v['--text-sub'] = sub;
    if (s.button_bg || s.cta_bg || s.primary_bg) v['--btn-bg'] = s.button_bg || s.cta_bg || s.primary_bg;
    if (s.button_text || s.cta_text || s.primary_text) v['--btn-text'] = s.button_text || s.cta_text || s.primary_text;
    return v;
}

// The style keys Trailhead actually reads — the editor shows only
// these while Trailhead is the site's template, so no colour picker
// sits there doing nothing.
export const TRAILHEAD_STYLE_KEYS = [
    'bg', 'text', 'eyebrow', 'heading', 'title', 'subtitle', 'body',
    'button_bg', 'button_text', 'cta_bg', 'cta_text', 'primary_bg', 'primary_text',
];

// "[Bracketed] words" are the accent word in every template.
export function accentParts(text) {
    const src = String(text ?? '');
    const parts = [];
    const re = /\[([^\]]+)\]/g;
    let last = 0;
    let m;
    while ((m = re.exec(src)) !== null) {
        if (m.index > last) parts.push({ accent: false, text: src.slice(last, m.index) });
        parts.push({ accent: true, text: m[1] });
        last = m.index + m[0].length;
    }
    if (last < src.length) parts.push({ accent: false, text: src.slice(last) });
    return parts;
}

export function plainText(text) {
    return String(text ?? '').replace(/\[([^\]]+)\]/g, '$1');
}

const STATUS_LABELS = { open: 'Open', caution: 'Caution', closed: 'Closed', low: 'Unknown' };

// site_data rows carry a `bucket` of open / caution / closed. Anything
// else (a forecast-less network, a missing row) reads as `low`, the
// design's neutral grey.
export function statusTone(bucket) {
    return ['open', 'caution', 'closed'].includes(bucket) ? bucket : 'low';
}

export function statusLabel(row) {
    return row?.label || STATUS_LABELS[statusTone(row?.bucket)];
}

export function initials(name) {
    const words = String(name ?? '').trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

export function isExternal(href) {
    if (isReportAppLink(href)) return false;
    return /^https?:\/\//i.test(String(href ?? ''));
}

// A link object as the editors store it: { label, href }. Blank label
// means "no button", which is how every optional CTA is switched off.
export function hasLink(link) {
    return !!(link && String(link.label ?? '').trim() !== '');
}

export function shortDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// A calendar date (YYYY-MM-DD) read as a LOCAL day — the one
// definition in lib/calendarDate.js, never `new Date('Y-m-d')`, which
// is UTC midnight and renders as the day before west of Greenwich.
export function weekdayShort(ymd) {
    const d = parseCalendarDate(ymd);
    return d ? d.toLocaleDateString('en-US', { weekday: 'short' }) : '';
}

/** "Drying · 22% wet", or '' when the trail has no reading (weather
 *  off, or no forecast for its ground). The label is the server's,
 *  the same words the Trail conditions page uses. */
export function wetnessLine(row) {
    if (row?.wetness_pct == null) return '';
    return [row.wetness_label, `${row.wetness_pct}% wet`].filter(Boolean).join(' · ');
}

/** "4.2 mi", in the org's own units, or '' when the trail has no
 *  stored length. */
export function distanceText(row) {
    const d = row?.distance;
    return d ? lengthText(d.value, d.unit) : '';
}
